import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, User, ArrowLeft } from 'lucide-react';
import { Header } from './Header';
import { SEO } from './SEO';

const BLOG_POST = {
  id: '1',
  title: 'Voice AI Agents: Transforming Restaurant Phone Management',
  slug: 'voice-ai-agents-transforming-restaurant-phone-management',
  content: `The restaurant industry is experiencing a technological revolution with the integration of Voice AI agents to handle incoming phone calls. These sophisticated systems are now capable of managing reservations, answering common questions about hours and menu items, and even processing takeout orders with remarkable accuracy. For restaurant owners struggling with staffing shortages and the need to focus on in-house customer service, Voice AI offers a compelling solution by ensuring no call goes unanswered while maintaining a personalized touch that customers appreciate. The technology has evolved beyond simple automated responses to understand context, manage complex conversations, and even detect customer sentiment to provide appropriate service levels.

What truly sets modern restaurant Voice AI apart is its seamless integration with existing restaurant management systems. When a customer calls to make a reservation, the AI agent can access the restaurant's booking system in real-time, check table availability, and confirm the booking while simultaneously updating the host's dashboard. For takeout orders, these systems can process menu selections, customize items according to dietary preferences, calculate pricing, and transfer all details directly to kitchen preparation systems. This end-to-end integration not only reduces errors that commonly occur during manual order taking but also collects valuable data on customer preferences and peak calling times, enabling restaurant managers to make informed operational decisions while focusing their human staff on creating exceptional in-person dining experiences.

Perhaps the most transformative aspect of Voice AI in the restaurant industry is its ability to handle multiple customer calls simultaneously, effectively eliminating the dreaded busy signal or hold times that frustrate hungry patrons. While a human receptionist can only speak with one caller at a time, Voice AI systems can engage with dozens of customers concurrently, taking reservations, answering questions, and processing orders all at once without any degradation in service quality. This capability not only ensures that restaurants never miss potential business due to missed calls but also provides a consistent level of service during unexpected surges in call volume, such as before holidays or special events. By efficiently managing peak calling periods without the need for additional staffing, Voice AI helps restaurants maximize revenue opportunities while maintaining customer satisfaction.`,
  author: 'RestroCalls Team',
  published_at: '2025-03-27T00:00:00Z',
  tags: ['Voice AI', 'Restaurant Technology', 'Customer Service', 'Innovation']
};

interface BlogPostProps {
  onContactClick: (e: React.MouseEvent) => void;
  onAuthClick: () => void;
  onDemoClick: () => void;
}

export function BlogPost({ onContactClick, onAuthClick, onDemoClick }: BlogPostProps) {
  const { slug } = useParams();
  const post = BLOG_POST;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-zinc-900">
      <SEO 
        title={`${post.title} - RestroCalls.AI Blog`}
        description={post.content.slice(0, 160) + '...'}
        path={`/blog/${post.slug}`}
        type="article"
      />
      <Header 
        onContactClick={onContactClick}
        onAuthClick={onAuthClick}
        onDemoClick={onDemoClick}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="mb-8">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>

        <h1 className="text-4xl font-bold text-white mb-6">{post.title}</h1>
        
        <div className="flex items-center gap-6 text-sm text-zinc-400 mb-12">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>{post.author}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>{new Date(post.published_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</span>
          </div>
        </div>

        <div className="prose prose-invert prose-emerald max-w-none">
          <div className="text-zinc-300 leading-relaxed space-y-6">
            {post.content.split('\n\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>

        {post.tags.length > 0 && (
          <div className="flex items-center gap-2 mt-12 pt-12 border-t border-zinc-800">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full text-sm border border-emerald-500/20"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}